import React, { useEffect, useMemo, useState } from 'react';

const COUNTRIES = [
  { code: 'TR', name: 'Turkey' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
];

export default function AdsTransparencyTest() {
  const [country, setCountry] = useState('TR');
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(0); // 0 = all
  const [q, setQ] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [tab, setTab] = useState('creatives'); // creatives | advertisers | weekly
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [previews, setPreviews] = useState({});
  // Autocomplete (disabled in new flow, kept for future)
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestQuery, setSuggestQuery] = useState('');
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState('');

  const renderCell = (v) => {
    if (v === null || v === undefined) return '-';
    if (v instanceof Date) return v.toISOString();
    if (typeof v === 'object') {
      try { return JSON.stringify(v); } catch (_) { return String(v); }
    }
    return String(v);
  };

  const displayItems = useMemo(() => items || [], [items]);

  const isImageUrl = (u) => typeof u === 'string' && /\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(u);
  const isVideoUrl = (u) => typeof u === 'string' && /\.(mp4|webm|ogg)(\?.*)?$/i.test(u);
  const renderPreview = (u, host) => {
    if (!u) return '-';
    const cached = previews[u];
    if (cached) {
      return (
        <img src={cached} alt="preview" className="w-28 h-20 object-cover rounded" referrerPolicy="no-referrer" />
      );
    }
    // Lazy-load preview for this specific URL
    // We avoid state updates during render by scheduling a microtask
    queueMicrotask(async () => {
      try {
        if (previews[u]) return; // race guard
        const r1 = await fetch(`/api/ads-transparency/preview?url=${encodeURIComponent(u)}`, { credentials: 'include' });
        const d1 = await r1.json().catch(() => ({}));
        if (d1 && d1.ok && d1.previewUrl) {
          setPreviews(prev => (prev[u] ? prev : { ...prev, [u]: d1.previewUrl }));
          return;
        }
        const r2 = await fetch(`/api/ads-transparency/preview/screenshot?url=${encodeURIComponent(u)}`, { credentials: 'include' });
        const d2 = await r2.json().catch(() => ({}));
        if (d2 && d2.ok && d2.screenshotBase64) {
          setPreviews(prev => (prev[u] ? prev : { ...prev, [u]: `data:image/png;base64,${d2.screenshotBase64}` }));
        }
      } catch (_) {
        // ignore
      }
    });
    if (isImageUrl(u)) {
      return (
        <img
          src={u}
          alt="preview"
          className="w-28 h-20 object-cover rounded"
          referrerPolicy="no-referrer"
        />
      );
    }
    if (isVideoUrl(u)) {
      return (
        <video
          src={u}
          className="w-28 h-20 object-cover rounded"
          muted
          playsInline
          loop
          controls={false}
          autoPlay
        />
      );
    }
    let domain = host || '';
    if (!domain) {
      try { domain = new URL(u).hostname; } catch (_) {}
    }
    const favicon = domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64` : '';
    return (
      <a href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
        {favicon ? <img src={favicon} alt="site" className="w-5 h-5 rounded" /> : null}
        <span className="text-gray-500">↗</span>
      </a>
    );
  };

  // Prefetch previews for creatives list
  useEffect(() => {
    if (tab !== 'creatives') return;
    const controller = new AbortController();
    const uniqueUrls = Array.from(new Set((items || []).map(r => r.creative_page_url).filter(Boolean)));
    uniqueUrls.slice(0, 100).forEach(async (u) => {
      if (previews[u]) return;
      try {
        const res = await fetch(`/api/ads-transparency/preview?url=${encodeURIComponent(u)}`, { signal: controller.signal, credentials: 'include' });
        const data = await res.json();
        if (data && data.ok && data.previewUrl) {
          setPreviews(prev => ({ ...prev, [u]: data.previewUrl }));
        } else {
          // fallback to screenshot
          const r2 = await fetch(`/api/ads-transparency/preview/screenshot?url=${encodeURIComponent(u)}`, { signal: controller.signal, credentials: 'include' });
          const d2 = await r2.json();
          if (d2 && d2.ok && d2.screenshotBase64) {
            setPreviews(prev => ({ ...prev, [u]: `data:image/png;base64,${d2.screenshotBase64}` }));
          }
        }
      } catch (_) { /* ignore */ }
    });
    return () => controller.abort();
  }, [tab, items]);

  const fetchJson = async (url) => {
    const res = await fetch(url, { credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    return data;
  };

  // Debounced advertiser suggestions (disabled for new flow; keep logic no-op unless suggestOpen controlled externally)
  useEffect(() => {
    let timer;
    const run = async () => {
      const term = (suggestQuery || '').trim();
      if (!term || term.length < 2) {
        setSuggestions([]); setSuggestOpen(false); return;
      }
      if (!(tab === 'creatives' || tab === 'advertisers')) return;
      try {
        setSuggestLoading(true);
        // Suggestions are restricted to Turkey (TR) and use the existing advertisers endpoint with q
        const params = new URLSearchParams({ country: 'TR', limit: '10', q: term });
        const d = await fetchJson(`/api/ads-transparency/political/advertisers?${params.toString()}`);
        const list = Array.isArray(d?.items) ? d.items.map(r => ({
          id: r.advertiser_id || r.advertiserId || r.id || r.advertiser_name,
          name: r.advertiser_name || r.name || r.advertiser_legal_name || ''
        })) : [];
        // Client-side safety filter (contains search term, case-insensitive)
        const termLower = term.toLowerCase();
        const filtered = list.filter(s => (s.name || '').toLowerCase().includes(termLower));
        setSuggestions(filtered.slice(0, 10));
        // Do not auto-open dropdown in new flow
      } catch (_) {
        setSuggestions([]); setSuggestOpen(false);
      } finally {
        setSuggestLoading(false);
      }
    };
    timer = setTimeout(run, 150); // debounce after Enter-triggered query
    return () => clearTimeout(timer);
  }, [suggestQuery, country, tab]);

  const handleSuggestionSelect = (s) => {
    if (!s) return;
    setQ(s.name || '');
    setSelectedAdvertiserId(s.id || '');
    setSuggestOpen(false);
    // Run creatives filtered by advertiserId for precise results
    runCreatives(s.id || '');
  };

  const runCreatives = async (advIdParam) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ country, days: String(days), limit: String(limit) });
      const advId = advIdParam || selectedAdvertiserId || '';
      if (advId) {
        params.set('advertiserId', advId);
      } else if (q && q.trim()) {
        params.set('q', q.trim());
      }
      if (!(Number(limit) > 0)) params.delete('limit');
      const d = await fetchJson(`/api/ads-transparency/political/creatives?${params.toString()}`);
      setItems(d?.items || []);
    } catch (e) { setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const runAdvertisers = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ country });
      if (Number(limit) > 0) params.set('limit', String(limit));
      if (q && q.trim()) params.set('q', q.trim());
      const d = await fetchJson(`/api/ads-transparency/political/advertisers?${params.toString()}`);
      let rows = d?.items || [];
      // Hard client-side guard: only keep names containing the typed term
      const term = (q || '').trim().toLowerCase();
      if (term) {
        rows = rows.filter(r => (r.advertiser_name || '').toLowerCase().includes(term));
      }
      setItems(rows);
    } catch (e) { setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const runWeekly = async () => {
    if (!advertiserId.trim()) { setError('Enter advertiser_id'); return; }
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ limit: String(limit) }).toString();
      const d = await fetchJson(`/api/ads-transparency/political/advertiser/${encodeURIComponent(advertiserId)}/weekly-spend?${q}`);
      setItems(d?.items || []);
    } catch (e) { setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  // Auto-fetch disabled; user triggers manually via buttons

  const handleSearchClick = () => {
    if (!(tab === 'creatives' || tab === 'advertisers')) return;
    // Open suggestions based on current query; user will select an advertiser
    setSuggestQuery((q || '').trim());
    setSuggestOpen(true);
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="p-6 bg-white border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/growth.svg" alt="bq" className="h-6 w-6" />
              <div className="text-lg font-semibold">Google Ads Transparency • BigQuery Test</div>
            </div>
            <div className="flex items-center gap-2">
              <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='creatives'?'bg-black text-white':'bg-white border'} `} onClick={()=>setTab('creatives')}>Creatives</button>
              <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='advertisers'?'bg-black text-white':'bg-white border'} `} onClick={()=>setTab('advertisers')}>Advertisers</button>
              <button className={`px-3 py-1.5 rounded-md text-sm ${tab==='weekly'?'bg-black text-white':'bg-white border'} `} onClick={()=>setTab('weekly')}>Weekly Spend</button>
            </div>
          </div>

          <div className="px-6 py-4 bg-white border-b flex flex-wrap items-center gap-3">
            <select value={country} onChange={(e)=>setCountry(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
              {COUNTRIES.map(c=> <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            {tab==='creatives' && (
              <input type="number" min={1} max={365} value={days} onChange={(e)=> setDays(Math.max(1, Math.min(365, Number(e.target.value)||30)))} className="px-3 py-2 border rounded-lg text-sm w-28" placeholder="Days" />
            )}
            <input type="number" min={0} value={limit} onChange={(e)=> setLimit(Math.max(0, Number(e.target.value)||0))} className="px-3 py-2 border rounded-lg text-sm w-28" placeholder="Limit (0 = all)" />
            {(tab==='creatives' || tab==='advertisers') && (
              <div className="relative">
                <input
                  value={q}
                  onChange={(e)=> setQ(e.target.value)}
                  onFocus={()=> {}}
                  onKeyDown={(e)=> {
                    // In the new flow, Enter does NOT trigger network; only Search button does.
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  className="px-3 py-2 border rounded-lg text-sm w-64"
                  placeholder={'search advertiser name'}
                />
                {/* Suggestions dropdown intentionally disabled in the new flow */}
              </div>
            )}
            {tab==='weekly' && (
              <input value={advertiserId} onChange={(e)=> setAdvertiserId(e.target.value)} className="px-3 py-2 border rounded-lg text-sm w-64" placeholder="advertiser_id (from advertisers list)" />
            )}
            {tab==='weekly' && <button className="px-3 py-2 rounded-md bg-black text-white" onClick={runWeekly}>Fetch weekly</button>}
            {/* Single Search flow: Always search advertisers by name when clicking Search */}
            <button className="px-3 py-2 rounded-md bg-black text-white" onClick={() => { setTab('advertisers'); runAdvertisers(); }}>Search</button>
            {tab==='advertisers' && <button className="px-3 py-2 rounded-md bg-white border" onClick={runAdvertisers}>Reload</button>}
          </div>

          <div className="px-6 py-4 bg-white">
            {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
            {loading && <div className="text-sm text-gray-600">Loading...</div>}
            {!loading && (
              <div className="overflow-auto border rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {tab==='creatives' && (<>
                        <th className="px-3 py-2 text-left">advertiser_disclosed_name</th>
                        <th className="px-3 py-2 text-left">preview</th>
                        <th className="px-3 py-2 text-left">url_host</th>
                      </>)}
                      {tab==='advertisers' && (<>
                        <th className="px-3 py-2 text-left">advertiser_id</th>
                        <th className="px-3 py-2 text-left">advertiser_name</th>
                        <th className="px-3 py-2 text-left">country</th>
                        <th className="px-3 py-2 text-left">actions</th>
                      </>)}
                      {tab==='weekly' && (<>
                        <th className="px-3 py-2 text-left">week_start</th>
                        <th className="px-3 py-2 text-left">spend</th>
                        <th className="px-3 py-2 text-left">currency</th>
                        <th className="px-3 py-2 text-left">country</th>
                      </>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(displayItems || []).slice(0, 50).map((r, i) => (
                      <tr key={i} className="odd:bg-white even:bg-gray-50">
                        {tab==='creatives' && (<>
                          <td className="px-3 py-2">{renderCell(r.advertiser_disclosed_name)}</td>
                          <td className="px-3 py-2">{renderPreview(r.creative_page_url, r.url_host)}</td>
                          <td className="px-3 py-2">{renderCell(r.url_host)}</td>
                        </>)}
                        {tab==='advertisers' && (<>
                          <td className="px-3 py-2">{renderCell(r.advertiser_id)}</td>
                          <td className="px-3 py-2">{renderCell(r.advertiser_name)}</td>
                          <td className="px-3 py-2">{renderCell(r.country_code)}</td>
                          <td className="px-3 py-2">
                            <button
                              className="px-2 py-1 rounded-md border text-xs"
                              onClick={() => { setSelectedAdvertiserId(r.advertiser_id); setTab('creatives'); runCreatives(r.advertiser_id); }}
                            >
                              View ads
                            </button>
                          </td>
                        </>)}
                        {tab==='weekly' && (<>
                          <td className="px-3 py-2">{renderCell(r.week_start_date)}</td>
                          <td className="px-3 py-2">{renderCell(r.spend)}</td>
                          <td className="px-3 py-2">{renderCell(r.currency)}</td>
                          <td className="px-3 py-2">{renderCell(r.country_code)}</td>
                        </>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && <div className="text-sm text-gray-500 p-4">No data</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


